import { SearchIcon } from '@components/Icons/SearchIcon';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useStationMode } from '@hooks/station';
import { useSearchSourceLocation } from '@hooks/useSearchSourceLocation';
import { useFavorites } from '@hooks/useFavorites';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { placeHolderData } from '../../config';
import {
  getAlbumById,
  getFavouriteAlbums,
  getFavouriteCompanionsPaginated,
  getFavouriteContributors,
  getFavouriteTracks,
  getTrackById,
  searchContent,
  searchMentors,
  SearchResult,
} from '../../services/api';
import { analytics } from '../../utils/analytics';
import {
  sendSearchResultsMessage,
  sendSelectCompanionMessage,
} from '@utils/station';
import { getTrackUrlForLocale } from '../../utils/localeUtils';

type FilterTab = 'all' | 'albums' | 'tracks' | 'contributors' | 'companions';

export const SearchPage = memo(() => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const searchTabParam = searchParams.get('searchTab') as FilterTab | null;
  const navigate = useNavigate();
  const location = useLocation();
  const isStationMode = useStationMode();

  // Get search source location using the hook
  const searchSourceLocation = useSearchSourceLocation(location);

  const [activeTab, setActiveTab] = useState<FilterTab>(() => {
    if (
      searchTabParam &&
      ['all', 'albums', 'tracks', 'contributors', 'companions'].includes(
        searchTabParam,
      )
    ) {
      return searchTabParam;
    }
    return 'all';
  });
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [favoriteKeys, setFavoriteKeys] = useState<Set<string>>(new Set());
  const [favoriteLoadingKeys, setFavoriteLoadingKeys] = useState<Set<string>>(
    new Set(),
  );
  const isInitialMount = useRef(true);

  const searchLocationRef = useRef<string>('');
  const totalResultsForTracking = useRef<number | null>(null);

  // Cache for search results per tab to avoid re-fetching
  const [resultsCache, setResultsCache] = useState<
    Record<FilterTab, SearchResult[]>
  >({
    all: [],
    albums: [],
    tracks: [],
    contributors: [],
    companions: [],
  });

  // Cache for hasMore state per tab
  const [hasMoreCache, setHasMoreCache] = useState<Record<FilterTab, boolean>>({
    all: false,
    albums: false,
    tracks: false,
    contributors: false,
    companions: false,
  });

  // Cache for offset per tab
  const [offsetCache, setOffsetCache] = useState<Record<FilterTab, number>>({
    all: 0,
    albums: 0,
    tracks: 0,
    contributors: 0,
    companions: 0,
  });

  const { setActivePopup } = useCommonState();
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { toggleFavorite } = useFavorites();

  // Play album functionality
  const playAlbum = useCallback(
    async (result: SearchResult) => {
      try {
        const fullAlbumData = await getAlbumById(result.id);
        if (
          !fullAlbumData ||
          !fullAlbumData.tracks ||
          fullAlbumData.tracks.length === 0
        ) {
          console.warn('No tracks found in album:', result.name);
          return;
        }

        const contributor = fullAlbumData.contributors?.[0];
        const firstTrack = fullAlbumData.tracks[0];

        // Add first track to recent tracks
        if (contributor) {
          const recentTrack = {
            track: {
              name: firstTrack.name,
              url: getTrackUrlForLocale(firstTrack, preferredLocale),
              trackIndex: 1,
            },
            contributor: {
              id: fullAlbumData.id,
              givenName: contributor.givenName || 'Unknown Artist',
              thumbnailUrl:
                contributor.thumbnailUrl ||
                placeHolderData.contributorThumbnail,
            },
            albumArtUrl:
              fullAlbumData?.thumbnailUrl ||
              fullAlbumData?.albumArtUrl ||
              contributor?.thumbnailUrl ||
              placeHolderData.albumCover,
            timestamp: Date.now(),
          };
          addRecentTrack(recentTrack);
        }

        const playlist = {
          currentIndex: 0,
          album: fullAlbumData,
        };
        setActivePlaylist(playlist);
        setActivePopup('musicplayer');
      } catch (error) {
        console.error('Error playing album:', error);
      }
    },
    [getAlbumById, setActivePlaylist, addRecentTrack, preferredLocale],
  );

  // Play track functionality
  const playTrack = useCallback(
    async (result: SearchResult) => {
      try {
        const trackInfo = await getTrackById(result.id);
        if (!trackInfo) {
          console.warn('Track not found:', result.name);
          return;
        }

        if (trackInfo) {
          // Create a track object with the new locale-aware structure
          const trackWithUrls = {
            id: trackInfo.id,
            name: trackInfo.trackName,
            duration: 0, // Duration not available in favorite track data
            isLiked: trackInfo.isLiked ?? false,
            // Use the new urls structure from the API response, with fallback for backward compatibility
            urls: trackInfo.audioUrl?.urls || [],
            // Keep the old url property as fallback if urls array is empty
            url:
              trackInfo.audioUrl?.urls && trackInfo.audioUrl.urls.length > 0
                ? undefined
                : trackInfo.audioUrl?.path +
                  '/' +
                  trackInfo.audioUrl?.fileName +
                  '.mp3',
          };

          // Create a single-track album for this favorite track
          const singleTrackAlbum = {
            id: trackInfo?.album?.id,
            albumName: trackInfo.album.name,
            albumArtUrl:
              trackInfo?.album?.thumbnailUrl || trackInfo?.album?.albumArtUrl,
            contributors: trackInfo.contributors,
            tracks: [trackWithUrls],
            category: 'favorites',
            likes: 0,
            isLiked: trackInfo.isLiked ?? false,
            upc: '',
            pk: '',
            description: '',
            genre: [],
            type: 'search',
          };

          const playlist = {
            currentIndex: 0,
            album: singleTrackAlbum,
          };
          const contributor = trackInfo.contributors[0];
          // Add to recent tracks
          const recentTrack = {
            track: {
              name: trackInfo.trackName,
              url: getTrackUrlForLocale(trackWithUrls, preferredLocale),
              trackIndex: 1,
            },
            contributor: {
              id: contributor?.id,
              givenName: contributor?.givenName || 'Unknown Artist',
              thumbnailUrl:
                contributor?.thumbnailUrl ||
                placeHolderData.contributorThumbnail,
            },
            albumArtUrl:
              trackInfo?.album?.thumbnailUrl ||
              trackInfo?.album?.albumArtUrl ||
              contributor?.thumbnailUrl ||
              placeHolderData.albumCover,
            timestamp: Date.now(),
          };
          addRecentTrack(recentTrack);

          setActivePlaylist(playlist);
          setActivePopup('musicplayer');
        }
      } catch (error) {
        console.error('Error playing track:', error);
      }
    },
    [getTrackById, setActivePlaylist, addRecentTrack, preferredLocale],
  );

  // Handle contributor click
  const handleContributorClick = useCallback(
    (result: SearchResult) => {
      navigate(`/contributor/${result.id}`);
    },
    [navigate],
  );

  // Filter results based on active tab
  const filteredResults = results.filter((result) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'albums') return result.type === 'Album';
    if (activeTab === 'tracks') return result.type === 'Track';
    if (activeTab === 'contributors') return result.type === 'Artist';
    if (activeTab === 'companions') return result.type === 'Mentor';
    return true;
  });

  const getResultFavoriteKey = useCallback((result: SearchResult) => {
    if (result.type === 'Album') return `album:${result.id}`;
    if (result.type === 'Track') return `track:${result.id}`;
    if (result.type === 'Artist') return `contributor:${result.id}`;
    return `companion:${result.id}`;
  }, []);

  const isResultFavorited = useCallback(
    (result: SearchResult) => favoriteKeys.has(getResultFavoriteKey(result)),
    [favoriteKeys, getResultFavoriteKey],
  );

  useEffect(() => {
    const loadFavorites = async () => {
      if (!isAuthenticated) {
        setFavoriteKeys(new Set());
        return;
      }

      try {
        const [albumsRes, tracksRes, contributorsRes, companionsRes] =
          await Promise.all([
            getFavouriteAlbums(),
            getFavouriteTracks(),
            getFavouriteContributors(),
            getFavouriteCompanionsPaginated(200, 0),
          ]);

        const keys = new Set<string>();
        (albumsRes?.items || []).forEach((item: any) =>
          keys.add(`album:${item.id}`),
        );
        (tracksRes?.items || []).forEach((item: any) =>
          keys.add(`track:${item.id}`),
        );
        (contributorsRes?.items || []).forEach((item: any) =>
          keys.add(`contributor:${item.id}`),
        );
        (companionsRes?.items || []).forEach((item: any) =>
          keys.add(`companion:${item.id}`),
        );
        setFavoriteKeys(keys);
      } catch (error) {
        console.error('Failed to load search favorites:', error);
      }
    };

    loadFavorites();
  }, [isAuthenticated]);

  const handleToggleFavorite = useCallback(
    async (result: SearchResult) => {
      const key = getResultFavoriteKey(result);
      const currentlyLiked = isResultFavorited(result);
      const favoriteType =
        result.type === 'Album'
          ? 'album'
          : result.type === 'Track'
            ? 'track'
            : result.type === 'Artist'
              ? 'contributor'
              : 'companion';

      setFavoriteLoadingKeys((prev) => new Set(prev).add(key));
      setFavoriteKeys((prev) => {
        const next = new Set(prev);
        if (currentlyLiked) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });

      try {
        await toggleFavorite(
          favoriteType,
          result.id,
          currentlyLiked,
          result.name,
          result.contributors?.[0]?.givenName,
          result.category,
          result.slug,
        );
      } catch (error) {
        setFavoriteKeys((prev) => {
          const next = new Set(prev);
          if (currentlyLiked) {
            next.add(key);
          } else {
            next.delete(key);
          }
          return next;
        });
      } finally {
        setFavoriteLoadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [getResultFavoriteKey, isResultFavorited, toggleFavorite],
  );

  // Request ID to track latest request and ignore stale responses
  const requestIdRef = useRef<number>(0);

  // Search function
  const performSearch = useCallback(
    async (
      searchQuery: string,
      searchOffset: number = 0,
      append: boolean = false,
      tab: FilterTab = activeTab,
    ) => {
      if (!searchQuery.trim()) return;

      // Increment request ID for this search
      const currentRequestId = ++requestIdRef.current;

      // Use different loading states for initial search vs load more
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        if (tab === 'companions') {
          // Search only mentors
          const mentorResults = await searchMentors(searchQuery, 12);

          // Ignore stale response if a newer request was made
          if (requestIdRef.current !== currentRequestId) return;

          const newOffset = searchOffset + mentorResults.length;
          if (append) {
            setResults((prev) => {
              const newResults = [...prev, ...mentorResults];
              setResultsCache((prevCache) => ({
                ...prevCache,
                companions: newResults,
              }));
              return newResults;
            });
          } else {
            setResults(mentorResults);
            setResultsCache((prev) => ({
              ...prev,
              companions: mentorResults,
            }));
          }
          setHasMore(false); // Mentors API doesn't support pagination yet
          setHasMoreCache((prev) => ({ ...prev, companions: false }));
          setOffset(newOffset);
          setOffsetCache((prev) => ({ ...prev, companions: newOffset }));
        } else {
          // For 'all' tab - no type filter needed
          if (tab === 'all') {
            if (!append) {
              // Initial search: fetch content and mentors in parallel
              const [contentResponse, mentorResults] = await Promise.all([
                searchContent(searchQuery, undefined, 12, searchOffset),
                searchMentors(searchQuery, 12),
              ]);

              // Ignore stale response if a newer request was made
              if (requestIdRef.current !== currentRequestId) return;

              const allResults = [...contentResponse.items, ...mentorResults];
              const newOffset = contentResponse.items.length;
              setResults(allResults);
              totalResultsForTracking.current =
                contentResponse.pagination.total + mentorResults.length;
              setResultsCache((prev) => ({
                ...prev,
                all: allResults,
              }));
              setOffset(newOffset);
              setOffsetCache((prev) => ({ ...prev, all: newOffset }));
              setHasMore(contentResponse.pagination.hasNext);
              setHasMoreCache((prev) => ({
                ...prev,
                all: contentResponse.pagination.hasNext,
              }));
            } else {
              // Load more: fetch content only (mentors don't support pagination)
              const contentResponse = await searchContent(
                searchQuery,
                undefined, // No type filter for 'all' tab
                12,
                searchOffset,
              );

              // Ignore stale response if a newer request was made
              if (requestIdRef.current !== currentRequestId) return;

              const newOffset = searchOffset + contentResponse.items.length;
              setResults((prev) => {
                const newResults = [...prev, ...contentResponse.items];
                setResultsCache((prevCache) => ({
                  ...prevCache,
                  all: newResults,
                }));
                return newResults;
              });
              setHasMore(contentResponse.pagination.hasNext);
              setHasMoreCache((prev) => ({
                ...prev,
                all: contentResponse.pagination.hasNext,
              }));
              setOffset(newOffset);
              setOffsetCache((prev) => ({ ...prev, all: newOffset }));
            }
          } else {
            // Search content for specific type (albums, tracks, contributors)
            const response = await searchContent(
              searchQuery,
              tab === 'albums'
                ? 'Album'
                : tab === 'tracks'
                  ? 'Track'
                  : 'Artist',
              12,
              searchOffset,
            );

            // Ignore stale response if a newer request was made
            if (requestIdRef.current !== currentRequestId) return;

            const newOffset = searchOffset + response.items.length;
            if (append) {
              setResults((prev) => {
                const newResults = [...prev, ...response.items];
                setResultsCache((prevCache) => ({
                  ...prevCache,
                  [tab]: newResults,
                }));
                return newResults;
              });
            } else {
              setResults(response.items);
              totalResultsForTracking.current = response.pagination.total;
              setResultsCache((prev) => ({
                ...prev,
                [tab]: response.items,
              }));
            }

            setHasMore(response.pagination.hasNext);
            setHasMoreCache((prev) => ({
              ...prev,
              [tab]: response.pagination.hasNext,
            }));
            setOffset(newOffset);
            setOffsetCache((prev) => ({ ...prev, [tab]: newOffset }));
          }
        }
      } catch (error) {
        // Ignore errors from stale requests
        if (requestIdRef.current !== currentRequestId) return;

        console.error('Search error:', error);
        if (!append) {
          setResults([]);
        }
      } finally {
        // Only update loading state if this is still the current request
        if (requestIdRef.current === currentRequestId) {
          if (append) {
            setLoadingMore(false);
          } else {
            setLoading(false);
            setHasSearched(true); // Mark that we've completed at least one search
          }
        }
      }
    },
    [],
  );

  // Update active tab when searchTab param changes
  useEffect(() => {
    if (
      searchTabParam &&
      ['all', 'albums', 'tracks', 'contributors', 'companions'].includes(
        searchTabParam,
      )
    ) {
      setActiveTab(searchTabParam);
    }
  }, [searchTabParam]);

  // Send search results via bridge in Station mode
  useEffect(() => {
    if (isStationMode && filteredResults.length > 0 && query) {
      const bridgeData = {
        type: 'search_results',
        query: query,
        tab: activeTab,
        results: filteredResults.map((result) => {
          const baseResult: any = {
            id: result.id,
            name: result.name,
            type: result.type,
            description: result.description,
          };

          if (result.type === 'Album') {
            baseResult.category = result.category;
          } else if (result.type === 'Track') {
            baseResult.albumName = result.albumName;
          } else if (result.type === 'Mentor') {
            baseResult.slug = result.slug;
            baseResult.jobTitle = result.jobTitle;
            baseResult.headline = result.headline;
            baseResult.callToAction = result.callToAction;
          }

          return baseResult;
        }),
        trigger_response: true,
      };
      sendSearchResultsMessage(bridgeData);
    }
  }, [isStationMode, filteredResults, query, activeTab]);

  // Track the last searched query to detect query changes
  const lastQueryRef = useRef<string>('');

  // Initial search when query or tab changes
  useEffect(() => {
    if (query) {
      const isNewQuery = lastQueryRef.current !== query;

      // If query changed, clear all caches
      if (isNewQuery) {
        lastQueryRef.current = query;
        setResultsCache({
          all: [],
          albums: [],
          tracks: [],
          contributors: [],
          companions: [],
        });
        setHasMoreCache({
          all: false,
          albums: false,
          tracks: false,
          contributors: false,
          companions: false,
        });
        setOffsetCache({
          all: 0,
          albums: 0,
          tracks: 0,
          contributors: 0,
          companions: 0,
        });
        // Clear results immediately for new query
        setResults([]);
        setLoading(true);
        setHasSearched(false); // Reset search state for new query
        isInitialMount.current = true; // Reset to track new search
        // Record search entry source for each new query
        searchLocationRef.current = searchSourceLocation;
      } else {
        // Same query, different tab - check cache
        const cachedResults = resultsCache[activeTab];
        if (cachedResults.length > 0) {
          setResults(cachedResults);
          // Also restore hasMore and offset from cache
          setHasMore(hasMoreCache[activeTab]);
          setOffset(offsetCache[activeTab]);
          setLoading(false);
          return; // Use cached results, don't re-fetch
        } else {
          // Show loading for uncached tab and clear old results to avoid filtering mismatch
          setResults([]);
          setLoading(true);
        }
      }

      setOffset(0);
      performSearch(query, 0, false, activeTab);
    } else {
      // Clear results and caches when query is empty
      setResults([]);
      setLoading(false);
      lastQueryRef.current = '';

      setResultsCache({
        all: [],
        albums: [],
        tracks: [],
        contributors: [],
        companions: [],
      });
      setHasMoreCache({
        all: false,
        albums: false,
        tracks: false,
        contributors: false,
        companions: false,
      });
      setOffsetCache({
        all: 0,
        albums: 0,
        tracks: 0,
        contributors: 0,
        companions: 0,
      });
      setHasMore(false);
      setOffset(0);
      setHasSearched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, activeTab]);

  // Track search performed event after search results are received
  useEffect(() => {
    if (
      query.trim() &&
      hasSearched &&
      isInitialMount.current &&
      totalResultsForTracking.current !== null
    ) {
      analytics.logEvent('search_performed', {
        search_term: query.trim(),
        search_results_count: totalResultsForTracking.current,
        search_location: searchLocationRef.current,
      });
      isInitialMount.current = false;
      totalResultsForTracking.current = null; // Reset after tracking
    }
  }, [query, hasSearched]);

  // Handle result click
  const handleResultClick = useCallback(
    (result: SearchResult, rankInResults?: number) => {
      // Track search result click
      analytics.logEvent('search_result_clicked', {
        search_term: query.trim(),
        content_id: result.id,
        content_type: result.type,
        rank_in_results: rankInResults ?? -1,
      });

      if (result.type === 'Mentor' && isStationMode) {
        sendSelectCompanionMessage(result.id);
        return;
      }

      if (result.type === 'Album') {
        playAlbum(result);
      } else if (result.type === 'Track') {
        playTrack(result);
      } else if (result.type === 'Artist') {
        handleContributorClick(result);
      } else if (result.type === 'Mentor') {
        navigate(`/${result.slug}?start=true`);
      }
    },
    [playAlbum, playTrack, handleContributorClick, navigate, isStationMode, query],
  );

  return (
    <>
      {isStationMode && (
        <Link
          to="/"
          className="fixed hidden md:block top-[17px] md:top-[17px] left-4 md:left-4 h-8 w-auto opacity-100 z-[99999]">
          <img
            src="/assets/images/logo_napster_white.svg"
            alt="Napster"
            className="h-8"
          />
        </Link>
      )}
      <div className="flex flex-col h-full pt-0 sm:pt-[10px]">
        {/* Header - Similar to Favorites */}
        <div className="flex-shrink-0">
          <div className="flex items-center justify-between px-4 sm:px-6">
            <div className="flex flex-col">
              <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
                {t('searchPage.title')}
              </h1>
              {query && (
                <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
                  {t('searchPage.showingResults', { query })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Filter Tabs - Similar to Favorites */}
        <div className="flex gap-4 px-4 sm:px-6 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap font-[NapsterSans] ${
              activeTab === 'all'
                ? 'bg-white text-black'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}>
            {t('searchPage.tabAll')}
          </button>
          <button
            onClick={() => setActiveTab('albums')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap font-[NapsterSans] ${
              activeTab === 'albums'
                ? 'bg-white text-black'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}>
            {t('searchPage.tabAlbums')}
          </button>
          <button
            onClick={() => setActiveTab('tracks')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap font-[NapsterSans] ${
              activeTab === 'tracks'
                ? 'bg-white text-black'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}>
            {t('searchPage.tabTracks')}
          </button>
          <button
            onClick={() => setActiveTab('contributors')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap font-[NapsterSans] ${
              activeTab === 'contributors'
                ? 'bg-white text-black'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}>
            {t('searchPage.tabContributors')}
          </button>
          <button
            onClick={() => setActiveTab('companions')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap font-[NapsterSans] ${
              activeTab === 'companions'
                ? 'bg-white text-black'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}>
            {t('searchPage.tabCompanions')}
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-[200px] na-scroll-container">
          {loading &&
          filteredResults.length === 0 &&
          resultsCache[activeTab].length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <NapsterSpinner size="large" />
            </div>
          ) : filteredResults.length === 0 &&
            !loading &&
            resultsCache[activeTab].length === 0 &&
            results.length === 0 &&
            (!query || hasSearched) ? (
            <div className="flex flex-col items-center justify-center h-full px-6">
              <SearchIcon />
              <h3 className="text-white text-lg font-medium mt-4 mb-2 font-[NapsterSans]">
                {query ? t('searchPage.noResults') : t('searchPage.startSearching')}
              </h3>
              <p className="text-white/60 text-center font-[NapsterSans]">
                {query
                  ? t('searchPage.noResultsFor', { query })
                  : t('searchPage.searchHint')}
              </p>
            </div>
          ) : (
            <div>
              {/* Grid Layout - Similar to Music Page */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredResults.map((result, index) => (
                  <div
                    key={`${result.type}-${result.id}-${index}`}
                    className="group cursor-pointer"
                    onClick={() => handleResultClick(result, index)}>
                    {/* Card Container */}
                    <div className="relative">
                      {/* Image Container */}
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-white/10 mb-3">
                        <img
                          src={
                            result.albumArtUrl ||
                            result.thumbnailUrl ||
                            placeHolderData.albumCover
                          }
                          alt={result.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              placeHolderData.albumCover;
                          }}
                        />

                        {/* Type Badge */}
                        <div
                          className={`absolute top-2 left-2 px-2 py-1 text-xs rounded-full font-[NapsterSans] font-medium ${
                            result.type === 'Album'
                              ? 'bg-blue-500/90 text-white'
                              : result.type === 'Track'
                                ? 'bg-green-500/90 text-white'
                                : result.type === 'Mentor'
                                  ? 'bg-orange-500/90 text-white'
                                  : 'bg-purple-500/90 text-white'
                          }`}>
                          {result.type === 'Artist'
                            ? t('searchPage.contributor')
                            : result.type === 'Mentor'
                              ? t('searchPage.companion')
                              : result.type}
                        </div>

                        {/* Favorite toggle */}
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            handleToggleFavorite(result);
                          }}
                          disabled={favoriteLoadingKeys.has(
                            getResultFavoriteKey(result),
                          )}
                          className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/75 transition-colors disabled:opacity-60">
                          {favoriteLoadingKeys.has(
                            getResultFavoriteKey(result),
                          ) ? (
                            <NapsterSpinner size="small" />
                          ) : isResultFavorited(result) ? (
                            <Likedicon width={14} height={14} />
                          ) : (
                            <FavoriteIcon width={14} height={14} />
                          )}
                        </button>
                      </div>

                      {/* Content */}
                      <div className="space-y-1">
                        <h3 className="text-white font-medium text-sm truncate font-[NapsterSans]">
                          {result.name}
                        </h3>
                        {result.contributors &&
                          result.contributors.length > 0 && (
                            <p className="text-white/60 text-xs truncate font-[NapsterSans]">
                              {result.contributors[0].givenName}
                            </p>
                          )}
                        {result.type === 'Track' && result.albumName && (
                          <p className="text-white/40 text-xs truncate font-[NapsterSans]">
                            {t('searchPage.from', { name: result.albumName })}
                          </p>
                        )}
                        {result.type === 'Mentor' && result.jobTitle && (
                          <p className="text-white/60 text-xs truncate font-[NapsterSans]">
                            {result.jobTitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && filteredResults.length > 0 && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() =>
                      performSearch(query, offset, true, activeTab)
                    }
                    disabled={loadingMore}
                    className="px-6 py-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors disabled:opacity-50 font-[NapsterSans] flex items-center gap-2">
                    {loadingMore && <NapsterSpinner size="small" />}
                    {loadingMore ? t('common.loading') : t('searchPage.loadMore')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

SearchPage.displayName = 'SearchPage';
